import { S } from './state.js';
import { ctx } from './canvas.js';
import { Audio } from './audio.js';
import { aim, pointIn } from './input.js';
import { roundRect } from './screens.js';
import { hexA } from './render.js';
import { fitText } from './ui.js';
import { recomputeStats } from './stats.js';
import { weaponStatNote } from './data/weapons.js';
import { TALENT_TREES, TALENT_MAP, treePoints, tierUnlocked, canSpend, talentLocked } from './data/talents.js';
// ---- Per-run TALENT TREE overlay (old-school WoW style). One archetype tree per tab; tiers gated by
// points-spent-in-tree (5/tier); ranked nodes; prereq arrows; a keystone at tier 4. Spending folds live
// through recomputeStats (mirrors applyStatCard). Free per-run respec via Reset Tree. See data/talents.js.

export function openTalents() { S.showTalents = true; S.talentScroll = 0; }
export function spendTalent(key) {   // take one rank (validated); applies immediately
  if (!canSpend(key)) return;
  S.talentRanks[key] = (S.talentRanks[key] || 0) + 1;
  S.talentPts--;
  recomputeStats();
  Audio.play('powerup');
}
export function refundTree(arch) {   // free per-run respec: hand every spent point in this tree back
  const tr = TALENT_TREES[arch]; if (!tr) return;
  let back = 0;
  for (const t of tr.talents) { const r = S.talentRanks[t.key] || 0; if (r) { back += r; delete S.talentRanks[t.key]; } }
  if (back) { S.talentPts += back; recomputeStats(); Audio.play('click'); }
}

function arrow(x1, y1, x2, y2, col) {
  ctx.strokeStyle = col; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
  const a = Math.atan2(y2 - y1, x2 - x1);
  ctx.beginPath(); ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - 9 * Math.cos(a - 0.4), y2 - 9 * Math.sin(a - 0.4));
  ctx.lineTo(x2 - 9 * Math.cos(a + 0.4), y2 - 9 * Math.sin(a + 0.4));
  ctx.closePath(); ctx.fillStyle = col; ctx.fill();
}

export function talentMenu() {
  S.uiButtons = [];
  ctx.fillStyle = 'rgba(6,10,24,.93)'; ctx.fillRect(0, 0, S.W, S.H);
  const pw = Math.min(820, S.W - 24), ph = Math.min(680, S.H - 24);
  const px = (S.W - pw) / 2, py = (S.H - ph) / 2;
  ctx.fillStyle = 'rgba(12,18,38,.97)'; roundRect(px, py, pw, ph, 20); ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,.08)'; ctx.lineWidth = 1; ctx.stroke();

  // ---- title + points ----
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#fff'; ctx.font = 'bold 24px system-ui';
  ctx.fillText('✦ TALENTS', px + 24, py + 40);
  const pts = S.talentPts || 0;
  ctx.font = 'bold 16px system-ui'; ctx.fillStyle = pts > 0 ? '#ffd24a' : '#6b76a0';
  ctx.fillText(pts + (pts === 1 ? ' point' : ' points') + ' to spend', px + 168, py + 40);

  // ✕ close
  { const cs = 30, cxx = px + pw - cs - 16, cyy = py + 18;
    ctx.fillStyle = 'rgba(255,255,255,.08)'; roundRect(cxx, cyy, cs, cs, 8); ctx.fill();
    ctx.fillStyle = '#9fb0e0'; ctx.font = 'bold 15px system-ui';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText('✕', cxx + cs / 2, cyy + cs / 2 + 1);
    S.uiButtons.push({ x: cxx, y: cyy, w: cs, h: cs, action: () => { S.showTalents = false; } }); }

  // ---- archetype tabs (one row of 10 icons; points-in-tree badge) ----
  const archKeys = Object.keys(TALENT_TREES);
  const tGap = 5, tw = (pw - 32 - tGap * (archKeys.length - 1)) / archKeys.length, tyy = py + 54, th = 34;
  let _tx = px + 16;
  for (const ak of archKeys) {
    const tr = TALENT_TREES[ak], active = S.talentTab === ak, sp = treePoints(ak);
    ctx.fillStyle = active ? hexA(tr.color, 0.28) : 'rgba(255,255,255,.05)'; roundRect(_tx, tyy, tw, th, 8); ctx.fill();
    if (active) { ctx.strokeStyle = tr.color; ctx.lineWidth = 1.5; ctx.stroke(); }
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.font = '17px system-ui'; ctx.fillStyle = '#fff'; ctx.fillText(tr.icon, _tx + tw / 2, tyy + th / 2);
    if (sp > 0) { ctx.font = 'bold 9px system-ui'; ctx.fillStyle = hexA(tr.color, 1); ctx.textAlign = 'right'; ctx.fillText(sp + '', _tx + tw - 3, tyy + th - 5); }
    const k = ak; S.uiButtons.push({ x: _tx, y: tyy, w: tw, h: th, action: () => { S.talentTab = k; S.talentScroll = 0; } });
    _tx += tw + tGap;
  }

  const tree = TALENT_TREES[S.talentTab], tpts = treePoints(S.talentTab);
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = hexA(tree.color, 1); ctx.font = 'bold 14px system-ui';
  ctx.fillText(tree.icon + ' ' + tree.name + ' — ' + tpts + ' spent', px + 24, py + 108);
  ctx.fillStyle = '#6b76a0'; ctx.font = '11px system-ui';
  ctx.fillText('press N or Esc to close · click a node to learn a rank', px + 24, py + 124);

  // ---- clipped scroll viewport: the tier grid ----
  const vpX = px + 16, vpY = py + 134, vpW = pw - 32, vpH = ph - 134 - 60;   // 60 = footer band
  ctx.save(); roundRect(vpX, vpY, vpW, vpH, 12); ctx.clip();

  const nodeSize = Math.max(50, Math.min(72, Math.floor(vpW / 3) - 16));
  const bandH = 20 + nodeSize + 18 + 14;   // tier label + node + name + gap
  const colCX = (col) => vpX + (vpW / 3) * (col + 0.5);
  const tierTop = (tier) => 8 + (tier - 1) * bandH;     // content-relative
  const nodeXY = (t) => ({ x: colCX(t.col) - nodeSize / 2, y: vpY - S.talentScroll + tierTop(t.tier) + 20 });

  // arrows first (under the nodes)
  for (const t of tree.talents) if (t.req) for (const r of t.req) {
    const pr = TALENT_MAP[r.key]; if (!pr || pr.arch !== tree.arch) continue;
    const a = nodeXY(pr.talent), b = nodeXY(t);
    const met = (S.talentRanks[r.key] || 0) >= r.rank;
    arrow(a.x + nodeSize / 2, a.y + nodeSize, b.x + nodeSize / 2, b.y - 4, met ? hexA(tree.color, 0.8) : 'rgba(255,255,255,.18)');
  }

  // tier labels + nodes
  const maxTier = Math.max(...tree.talents.map(t => t.tier));
  for (let tier = 1; tier <= maxTier; tier++) {
    const ly = vpY - S.talentScroll + tierTop(tier) + 4;
    const unlocked = tierUnlocked(tree.arch, tier), need = 5 * (tier - 1);
    const isFork = tree.talents.some(t => t.tier === tier && t.fork);
    if (ly + bandH > vpY && ly < vpY + vpH) {
      ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
      ctx.fillStyle = unlocked ? '#9fb0e0' : '#5a6486'; ctx.font = 'bold 11px system-ui';
      ctx.fillText('TIER ' + tier + (need ? '  ·  ' + need + ' pts' + (unlocked ? '' : ' 🔒') : '') + (isFork ? '   ·   ⇄ PICK ONE PATH' : ''), vpX + 8, ly + 8);
      if (isFork) {   // a ⇄ between the two fork columns
        ctx.textAlign = 'center'; ctx.fillStyle = hexA(tree.color, unlocked ? 0.9 : 0.4); ctx.font = 'bold 20px system-ui';
        ctx.fillText('⇄', colCX(1), vpY - S.talentScroll + tierTop(tier) + 20 + nodeSize / 2);
      }
    }
  }
  for (const t of tree.talents) {
    const { x, y } = nodeXY(t);
    if (y + nodeSize < vpY || y > vpY + vpH) continue;   // cull
    const rank = S.talentRanks[t.key] || 0, maxed = rank >= t.max, take = canSpend(t.key);
    const lock = talentLocked(t.key);
    const dim = !maxed && !take && rank === 0 && lock && lock !== '';
    const border = maxed ? '#ffd24a' : (rank > 0 || take) ? tree.color : 'rgba(255,255,255,.18)';
    // keystone glow
    if (t.keystone) { ctx.save(); ctx.shadowColor = tree.color; ctx.shadowBlur = maxed ? 16 : take ? 12 : 4; }
    ctx.fillStyle = rank > 0 ? hexA(tree.color, 0.16) : 'rgba(255,255,255,.04)';
    roundRect(x, y, nodeSize, nodeSize, t.keystone ? 14 : 10); ctx.fill();
    ctx.strokeStyle = border; ctx.lineWidth = maxed ? 2.5 : take ? 2 : 1.4; ctx.stroke();
    if (t.keystone) ctx.restore();
    ctx.globalAlpha = dim ? 0.5 : 1;
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.font = Math.floor(nodeSize * 0.42) + 'px system-ui'; ctx.fillStyle = '#fff';
    ctx.fillText(t.icon, x + nodeSize / 2, y + nodeSize * 0.42);
    // rank badge
    ctx.font = 'bold 11px system-ui'; ctx.fillStyle = maxed ? '#ffd24a' : take ? '#7be08a' : '#9fb0e0';
    ctx.fillText(rank + '/' + t.max, x + nodeSize / 2, y + nodeSize - 12);
    // name under node
    ctx.font = '10px system-ui'; ctx.fillStyle = dim ? '#6b76a0' : '#cdd6ff';
    ctx.fillText(fitText(t.name, nodeSize + 18), x + nodeSize / 2, y + nodeSize + 11);
    ctx.globalAlpha = 1;
    // "+" affordance when takeable
    if (take) { ctx.fillStyle = '#7be08a'; ctx.font = 'bold 13px system-ui'; ctx.textAlign = 'right'; ctx.fillText('+', x + nodeSize - 5, y + 13); }
    // click to learn
    S.uiButtons.push({ x, y, w: nodeSize, h: nodeSize, action: () => spendTalent(t.key) });
    // hover tooltip (full desc + how its DELIVERY stats behave on the held weapon + next-rank/lock)
    let wnote = '';
    if (t.eff) for (const k in t.eff) { const n = weaponStatNote(k); if (n) wnote += '  ·  ' + k + ' → ' + n; }
    if (t.mut === 'shotgun') { const n = weaponStatNote('multishot'); if (n) wnote += '  ·  pellets → ' + n; }   // Scattergun fork adds pellets
    if (t.mut === 'rifle') { const n = weaponStatNote('pierce'); if (n) wnote += '  ·  pierce → ' + n; }          // Marksman Rifle fork adds pierce
    const nextNote = maxed ? ' · MAXED' : lock ? ' · 🔒 ' + lock : ' · click to learn (' + rank + '→' + (rank + 1) + ')';
    S.tooltips.push({ x, y, w: nodeSize, h: nodeSize, text: t.name + ' [' + rank + '/' + t.max + ']  ' + t.desc + wnote + nextNote });
  }
  ctx.restore();   // un-clip

  // scrollbar
  const contentH = 8 + maxTier * bandH + 6;
  S.talentScrollMax = Math.max(0, contentH - vpH);
  if (S.talentScroll > S.talentScrollMax) S.talentScroll = S.talentScrollMax;
  if (S.talentScrollMax > 0) {
    const thumbH = Math.max(30, vpH * vpH / contentH), thumbY = vpY + (S.talentScroll / S.talentScrollMax) * (vpH - thumbH);
    ctx.fillStyle = 'rgba(255,255,255,.14)'; roundRect(vpX + vpW - 5, thumbY, 4, thumbH, 2); ctx.fill();
  }

  // ---- footer: Reset Tree ----
  const fy = py + ph - 48, bw = Math.min(180, vpW * 0.5), bx = px + 24;
  const canReset = tpts > 0, hot = pointIn(aim, bx, fy, bw, 34);
  ctx.fillStyle = canReset ? (hot ? 'rgba(255,90,110,.35)' : 'rgba(255,90,110,.18)') : 'rgba(255,255,255,.05)';
  roundRect(bx, fy, bw, 34, 9); ctx.fill();
  ctx.fillStyle = canReset ? '#ff9aa6' : '#5a6486'; ctx.font = 'bold 13px system-ui';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText('↺ Reset ' + tree.name + ' (free)', bx + bw / 2, fy + 17);
  if (canReset) { const a = tree.arch; S.uiButtons.push({ x: bx, y: fy, w: bw, h: 34, action: () => refundTree(a) }); }
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
}
